import { useEffect, useState, type ReactNode } from 'react'
import { fallbackOf } from '@/lib/assetFallback'
import { toRelUrl } from '@/lib/mediaUrl'

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv|ogg)(\?|#|$)/i

/**
 * 这个地址是不是视频 —— **全站唯一一份**。
 *
 * 谁要用就用它，别在外面再写一个 mp4 正则：漏一个扩展名（`.m4v` / `.ogv`）
 * 的表现是「同一个文件在素材库里是视频、在参考图格子里变成碎图标」，
 * 而两处正则不放在一起，这种不一致根本查不出来。
 */
export const isVideoUrl = (u: string) => VIDEO_EXT.test(u)

/**
 * 带降级兜底的媒体组件。图片与视频共用。
 *
 * 三级降级链，每一级都是真实出现过的故障：
 *   1. 主地址（图床公网 URL 或本站相对地址）
 *   2. 本地副本（图床图被删/被墙时）—— 见 lib/assetFallback
 *   3. SVG 占位块（断网时保持排版不塌，而不是一排碎图标）
 *
 * **为什么必须认视频：**
 * 素材库 / 作品库 / 结果网格里混着视频。以前一律渲染 `<img>`，
 * 而 `<img src="x.mp4">` 是**必然失败**的（浏览器不会把视频当图片解码），
 * 于是视频卡永远落在占位块上，看着像"图挂了"。
 * 判定顺序：先看调用方传的 `kind`（最准），没传再从扩展名猜。
 *
 * 视频**只出首帧 + 播放角标，不做内联播放**：
 * 卡片本身是可点击的（素材库是"选中"，作品库是"打开"），
 * 再挂个播放器会和卡片点击抢事件。
 */
export function SmartImage({
  src,
  alt,
  kind,
  className,
  style,
  renderFallback,
}: {
  src: string
  alt: string
  kind?: string
  className?: string
  style?: React.CSSProperties
  /**
   * 三级链**全失败**时画什么。不给就用内置的渐变占位块（带 `alt` 文案）。
   *
   * 为什么留这个口子：`RefUploader` 的裂图判据是它自己的红叉 `.refup__broken`
   * —— `verify-upload.py` 里写死了「远程图加载中 `naturalWidth` 也是 0，
   * 唯一可认的裂图判据是组件的降级占位」。若这里一律替它画内置占位，
   * 那条探针会**恒读 0**：从「能发现裂图」退化成「永远报没有裂图」，
   * 典型的假绿。所以**占位长什么样由调用方给，降级链本身仍只此一份**。
   */
  renderFallback?: () => ReactNode
}) {
  const resolved = toRelUrl(src)
  const isVideo = kind === 'video' || isVideoUrl(resolved)

  const [state, setState] = useState<'loading' | 'ok' | 'error'>(resolved ? 'loading' : 'error')
  const [current, setCurrent] = useState(resolved)

  useEffect(() => {
    setCurrent(toRelUrl(src))
    setState(resolved ? 'loading' : 'error')
  }, [src, resolved])

  const handleError = () => {
    const local = fallbackOf(current)
    if (local && local !== current) {
      setCurrent(toRelUrl(local))
      setState('loading')
      return
    }
    setState('error')
  }

  if (state === 'error' || !current) {
    if (renderFallback) return <>{renderFallback()}</>
    return (
      <div
        className={className}
        style={{
          width: '100%',
          height: '100%',
          /* `min(80px, 100%)` 而不是裸 80：占位块要能撑住结果网格那种大格子，
             但塞进 44×44 的任务缩略图 / 素材库小格时**不许把格子顶高**
             （裸 80 会让占位溢出 36px，被外壳裁掉一截，看着像排版错位）。 */
          minHeight: 'min(80px, 100%)',
          display: 'grid',
          placeItems: 'center',
          background: 'linear-gradient(135deg, hsl(var(--primary) / .14), hsl(var(--muted)))',
          color: 'hsl(var(--primary-strong))',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.02em',
          textAlign: 'center',
          padding: 8,
          ...style,
        }}
      >
        {alt.slice(0, 22)}
      </div>
    )
  }

  // 图片保持原样（img 直接作子元素）—— 外面套层壳会打断 .asset__media img 这类既有样式
  if (!isVideo) {
    return (
      <>
        {state === 'loading' ? <Shimmer /> : null}
        <img
          src={current}
          alt={alt}
          className={className}
          style={style}
          loading="lazy"
          onLoad={() => setState('ok')}
          onError={handleError}
        />
      </>
    )
  }

  return (
    <div className="smartvideo" style={style}>
      {state === 'loading' ? <Shimmer /> : null}
      {/* #t=0.1 是为了逼浏览器解码出首帧 —— 只给 preload="metadata" 时很多情况下是黑块 */}
      <video
        src={`${current}${current.includes('#') ? '' : '#t=0.1'}`}
        className={className}
        muted
        playsInline
        preload="metadata"
        onLoadedData={() => setState('ok')}
        onError={handleError}
      />
      <span className="smartvideo__badge" title="视频">
        ▶
      </span>
    </div>
  )
}

function Shimmer() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(100deg, hsl(var(--muted)) 30%, hsl(var(--card)) 50%, hsl(var(--muted)) 70%)',
        animation: 'shimmer 1.2s infinite linear',
        backgroundSize: '200% 100%',
      }}
    />
  )
}
